// Pure helpers for the per-plan recurring generate action: calendar-day
// parsing, launch-scope comparison, request scope projection, disabled-reason
// copy, and message copy. No state and no rendering.
import type { PlanGenerateScope } from "@/components/plan-generate-recovery";

export function dayOnly(value: string | undefined): string | null {
  return value && /^\d{4}-\d{2}-\d{2}/.test(value) ? value.slice(0, 10) : null;
}

export function todayDay(): string {
  return new Date().toISOString().slice(0, 10);
}

export function samePlanGenerateScope(
  left: PlanGenerateScope,
  right: PlanGenerateScope,
): boolean {
  return (
    left.organization_id === right.organization_id &&
    left.user_id === right.user_id
  );
}

export function occurrenceRequestScope(scope: PlanGenerateScope): {
  organization_id: string;
} {
  return {
    organization_id: scope.organization_id,
  };
}

export function planGenerateDisabledReason(input: {
  held: boolean;
  busy: boolean;
  pendingElsewhere: boolean;
  heldElsewhere: boolean;
  status: string;
  nextDay: string | null;
  due: boolean;
}): string | null {
  if (input.held) {
    return "An earlier generation for this plan was not confirmed — check current status first.";
  }
  if (input.busy) return "Working…";
  if (input.pendingElsewhere) {
    return "Another plan in this organization has a generation in progress — wait for it to finish.";
  }
  if (input.heldElsewhere) {
    return "Another plan in this organization has an unconfirmed generation — resolve that plan first.";
  }
  if (input.status === "paused") {
    return "This plan is paused and sits out of the run.";
  }
  if (input.status === "completed") return "This plan is finished.";
  if (input.status !== "active") return "This plan is not active.";
  if (input.nextDay === null) return "This plan has no next run date.";
  if (!input.due) return `This plan is not due until ${input.nextDay}.`;
  return null;
}

export function scopedMutationBlockedMessage(): string {
  return "Another plan in this organization has a generation in progress or unconfirmed. Finish that plan first, then review this plan again.";
}

export function notConfirmedMessage(occurrenceDate: string): string {
  return `Generation was not confirmed. The plan may have changed or the response may have been interrupted. Check current status for ${occurrenceDate} before trying anything else — do not generate again yet.`;
}

export function heldPlanNotice(occurrenceDate: string): string {
  return `An earlier generation for ${occurrenceDate} was not confirmed. Check current status before generating or running anything else.`;
}

export function createdDraftMessage(noun: string, occurrenceDate: string): string {
  return `Draft ${noun} created for ${occurrenceDate}. It is a draft — this generation did not post, send, or pay it.`;
}

export function replayMessage(
  noun: string,
  occurrenceDate: string,
  documentStatus: string,
): string {
  return `No new draft was created for ${occurrenceDate}: the ${noun} already exists with status ${documentStatus}.`;
}

export function statusExistsMessage(
  noun: string,
  occurrenceDate: string,
  documentStatus: string,
): string {
  return `Current status: the ${noun} for ${occurrenceDate} is ${documentStatus}. This check only read the status; it made no new writes.`;
}

export function statusAbsentMessage(
  noun: string,
  occurrenceDate: string,
  nextRunDate: string | null,
): string {
  return nextRunDate === occurrenceDate
    ? `No draft ${noun} exists for ${occurrenceDate}. Review the plan and generate again if it is still due.`
    : `No draft ${noun} exists for ${occurrenceDate}. This plan's next run is ${nextRunDate ?? "a different date"}; review the plan before generating again.`;
}

export function statusCheckFailedMessage(): string {
  return "Current status could not be checked. Generating and running remain blocked; try the status check again.";
}
